import { OktaAuth } from '@okta/okta-auth-js'
import oidcConfig, { AuthProvider, getEnabledProviders } from '../oidc/config'
import {
  clearJumpCloudUser,
  getJumpCloudIdToken,
  getJumpCloudUserManager,
  hasStoredJumpCloudUser,
  JumpCloudSignInState,
} from '../oidc/jumpcloud'
import getEnv from './helpers/getEnv'
import Environment from './types/Environment'
import { User } from './types/User'

const ACCESS_TOKEN = 'accessToken'
const REFRESH_TOKEN = 'refreshToken'
const TOKEN_ORIGIN = 'tokenOrigin'
const OKTA_STORAGE_TOKEN = 'okta-token-storage'
const LAST_AUTOMATIC_SIGN_IN_TIME_KEY = 'lastAutomaticSignInTime'
// The identity provider returns within seconds while it still has a session, so another 401
// sooner than this means signing in again didn't help (e.g. the API rejects the token).
const MIN_TIME_BETWEEN_AUTOMATIC_SIGN_INS_MS = 60 * 1000

export const oktaAuth = new OktaAuth(oidcConfig.oidc)

export interface TokenManager {
  getToken: () => string | null
  clearTokens: () => void
  setAccessToken: (accessToken: any) => void
  setRefreshToken: (refreshToken: any) => void
  getRefreshToken: () => string | null
}

export const tokenManager: TokenManager = {
  getToken(): string | null {
    const { idToken: id } = JSON.parse(localStorage.getItem(OKTA_STORAGE_TOKEN) || '{}')
    return id?.idToken || localStorage.getItem(ACCESS_TOKEN)
  },

  clearTokens() {
    const storageKeys = [ACCESS_TOKEN, REFRESH_TOKEN, TOKEN_ORIGIN, OKTA_STORAGE_TOKEN]
    storageKeys.forEach(key => {
      localStorage.removeItem(key)
    })
  },

  setAccessToken(accessToken: any) {
    localStorage.setItem(ACCESS_TOKEN, accessToken)
  },

  setRefreshToken(refreshToken: any) {
    localStorage.setItem(REFRESH_TOKEN, refreshToken)
  },

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN)
  },
}

export interface AuthManager {
  startService: () => void
  signInWithRedirect: (provider?: AuthProvider) => Promise<void>
  logout: () => void
  isAuthenticated: () => boolean
  getToken: () => string | null
  getUser: () => Promise<User>
  subscribeOnAuthStateChange: (handler: any) => void
}

const oktaAuthManager: AuthManager = {
  startService: () => {},
  signInWithRedirect: async () => {
    await oktaAuth.signInWithRedirect({
      originalUri: window.location.href,
    })
  },
  logout: () => {
    oktaAuth.signOut()
    tokenManager.clearTokens()
    window.location.href = '/login'
  },
  isAuthenticated: () => {
    if (getEnv() === Environment.Test) return true
    return tokenManager.getToken() ? true : false
  },
  getToken: () => {
    return tokenManager.getToken()
  },
  getUser: async () => {
    return oktaAuth.getUser().then(user => {
      return {
        first_name: user.given_name || '',
        last_name: user.family_name || '',
        username: user.name,
        id: user.email,
      }
    })
  },
  subscribeOnAuthStateChange: handler => {
    oktaAuth.authStateManager.subscribe(handler)
  },
}

const ignoreTokenRevocationFailure = () => undefined

const jumpcloudAuthManager: AuthManager = {
  startService: () => {},
  signInWithRedirect: async () => {
    const jumpcloudSignInState: JumpCloudSignInState = { originalUri: window.location.href }
    await getJumpCloudUserManager().signinRedirect({ state: jumpcloudSignInState })
  },
  logout: () => {
    getJumpCloudUserManager()
      .revokeTokens()
      .catch(ignoreTokenRevocationFailure)
      .finally(() => {
        clearJumpCloudUser()
        window.location.href = '/login'
      })
  },
  isAuthenticated: () => {
    return getJumpCloudIdToken() ? true : false
  },
  getToken: () => {
    return getJumpCloudIdToken()
  },
  getUser: async () => {
    const jumpcloudUser = await getJumpCloudUserManager().getUser()
    const jumpcloudProfile = jumpcloudUser?.profile
    return {
      first_name: jumpcloudProfile?.given_name || '',
      last_name: jumpcloudProfile?.family_name || '',
      username: jumpcloudProfile?.name,
      id: jumpcloudProfile?.email,
    }
  },
  subscribeOnAuthStateChange: authStateChangeHandler => {
    const jumpcloudUserManager = getJumpCloudUserManager()
    jumpcloudUserManager.events.addUserLoaded(authStateChangeHandler)
    jumpcloudUserManager.getUser().then(existingJumpCloudUser => {
      const hasValidExistingSession =
        existingJumpCloudUser !== null && !existingJumpCloudUser.expired
      if (hasValidExistingSession) authStateChangeHandler()
    })
  },
}

export function getActiveProvider(): AuthProvider {
  const enabledProviders = getEnabledProviders()
  if (!enabledProviders.includes('jumpcloud')) return 'okta'
  if (!enabledProviders.includes('okta')) return 'jumpcloud'

  const lastSignedInWithJumpCloud = hasStoredJumpCloudUser()
  return lastSignedInWithJumpCloud ? 'jumpcloud' : 'okta'
}

export function clearSessionsExcept(signedInProvider: AuthProvider): void {
  if (signedInProvider === 'okta') {
    clearJumpCloudUser()
  } else {
    localStorage.removeItem(OKTA_STORAGE_TOKEN)
  }
}

const authManagersByProvider: Record<AuthProvider, AuthManager> = {
  okta: oktaAuthManager,
  jumpcloud: jumpcloudAuthManager,
}

function getActiveProviderAuthManager(): AuthManager {
  return authManagersByProvider[getActiveProvider()]
}

function notifyOnlyWhileProviderIsActive(
  provider: AuthProvider,
  authStateChangeHandler: (...handlerArgs: unknown[]) => void
) {
  return (...handlerArgs: unknown[]) => {
    if (getActiveProvider() === provider) authStateChangeHandler(...handlerArgs)
  }
}

export const authManager: AuthManager = {
  startService: () => {},
  signInWithRedirect: async (provider = getActiveProvider()) => {
    await authManagersByProvider[provider].signInWithRedirect()
  },
  logout: () => getActiveProviderAuthManager().logout(),
  isAuthenticated: () => getActiveProviderAuthManager().isAuthenticated(),
  getToken: () => getActiveProviderAuthManager().getToken(),
  getUser: () => getActiveProviderAuthManager().getUser(),
  subscribeOnAuthStateChange: authStateChangeHandler => {
    oktaAuthManager.subscribeOnAuthStateChange(
      notifyOnlyWhileProviderIsActive('okta', authStateChangeHandler)
    )
    if (getEnabledProviders().includes('jumpcloud')) {
      jumpcloudAuthManager.subscribeOnAuthStateChange(
        notifyOnlyWhileProviderIsActive('jumpcloud', authStateChangeHandler)
      )
    }
  },
}

export type AutomaticSignInOutcome = 'redirecting' | 'skippedToAvoidLoop'

let isAutomaticSignInRedirecting = false

function hasRecentAutomaticSignIn(): boolean {
  const lastAutomaticSignInTime = Number(
    sessionStorage.getItem(LAST_AUTOMATIC_SIGN_IN_TIME_KEY) || 0
  )
  return Date.now() - lastAutomaticSignInTime < MIN_TIME_BETWEEN_AUTOMATIC_SIGN_INS_MS
}

function forgetFailedAutomaticSignIn(): void {
  isAutomaticSignInRedirecting = false
  sessionStorage.removeItem(LAST_AUTOMATIC_SIGN_IN_TIME_KEY)
}

export async function signInAgainAfterUnauthorized(): Promise<AutomaticSignInOutcome> {
  if (isAutomaticSignInRedirecting) return 'redirecting'
  if (hasRecentAutomaticSignIn()) return 'skippedToAvoidLoop'

  sessionStorage.setItem(LAST_AUTOMATIC_SIGN_IN_TIME_KEY, String(Date.now()))
  isAutomaticSignInRedirecting = true
  try {
    await authManager.signInWithRedirect()
  } catch (signInRedirectError) {
    forgetFailedAutomaticSignIn()
    throw signInRedirectError
  }
  return 'redirecting'
}
